import cx from 'classnames';
import { useRef } from 'react';
import type { VoidFunctionComponent } from 'react';

import { window } from '../../../globals';
import useDispatchOnMount from '../../../hooks/useDispatchOnMount';
import OrganizationsStore from '../../../redux/organizations';
import SidebarLayout from '../../layouts/SidebarLayout';
import IssuesFilters from '../filters/IssuesFilters';
import type { IssuesContainerProps } from '../IssuesContainer';

import useSyncStateAndURLParams from './hooks/useSyncStateAndURLParams';
import IssuesInboxHeader from './IssuesInboxHeader';
import IssuesPagination from './IssuesPagination';
import IssuesTable from './IssuesTable';

import './IssuesInbox.less';

type Props = IssuesContainerProps & {
  /**
   * If true, then there will be no top padding around the inbox.
   */
  withNoPadding?: boolean;
};

/**
 * IssuesInbox is a view of all the issues, along with the filters
 * to allow users to find and take action on the issues which matter to them.
 * Depending on the Scope, they can be viewing issues for a given project,
 * or all issues across an org (ie: "global").
 */
const IssuesInbox: VoidFunctionComponent<Props> = ({ router, location, params, withNoPadding }) => {
  // We need to fetch the organization in order to load Jira's info.
  useDispatchOnMount(() => OrganizationsStore.actions.fetchOne(window.user?.organizationId as number));

  const tableRef = useRef<HTMLDivElement>(null);

  //  Handles keeping the URL params and state in sync.
  //  Note that on the initial render this will always dispatch the
  //  HYDRATE_STATE action, which will kick off the initial fetch of issues.
  useSyncStateAndURLParams({ router, location, params });

  return (
    <div className={cx('issues-inbox', { 'issues-inbox--no-padding': withNoPadding })} data-test-id="issues-inbox">
      <SidebarLayout>
        <SidebarLayout.Header>
          <IssuesInboxHeader />
        </SidebarLayout.Header>
        <SidebarLayout.Main>
          <IssuesTable tableRef={tableRef} />
          <IssuesPagination />
        </SidebarLayout.Main>
        <SidebarLayout.Sidebar>
          <IssuesFilters />
        </SidebarLayout.Sidebar>
      </SidebarLayout>
    </div>
  );
};

export default IssuesInbox;
